import { Icon, type IconSize } from '@mdvip/shared/components/blocks/Icon';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@mdvip/shared/components/ui/select';
import { cn } from '@mdvip/shared/lib/utils';
import { useId, useState } from 'react';

const PILL =
  'inline-flex items-center justify-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap shrink-0';

/* Sentinel value for the "Add new account" action. Choosing it closes the
   Select (via onValueChange) and fires onAddNew instead of selecting a method. */
const ADD_NEW = '__add_new_account__';

export interface PaymentAccountItem {
  id: string;
  label: string;
  /** Expired method, rendered in destructive styling (selecting it is an error). */
  expired?: boolean;
  /** The member's default method, shown with a Default badge. */
  isDefault?: boolean;
  /** "Check (mailed in)" sentinel, shown with a mail glyph. */
  check?: boolean;
  /** Bank account (checking/savings), shown with a bank glyph instead of a card. */
  bank?: boolean;
}

export interface PickerPaymentAccountProps {
  items: PaymentAccountItem[];
  value?: string;
  onChange?: (id: string) => void;
  /** Renders an inline "Add new account" action at the foot of the menu. */
  onAddNew?: () => void;
  /** Floating label. @default 'Payment account' */
  label?: string;
  /** Error styling (e.g. an expired selection). @default false */
  error?: boolean;
  addNewLabel?: string;
  /** Badge text for an expired method. @default 'Expired' */
  expiredLabel?: string;
  /** Badge text for the member's default method. @default 'Default' */
  defaultLabel?: string;
  className?: string;
}

/** Leading glyph for an account row: mail (check), bank, or card. Tinted destructive when expired. */
function AccountGlyph({
  item,
  size = 'md',
  className,
}: {
  item?: PaymentAccountItem;
  size?: IconSize;
  className?: string;
}) {
  const name = item?.check ? 'mail' : item?.bank ? 'landmark' : 'creditCard';
  return (
    <Icon
      name={name}
      size={size}
      className={cn(item?.expired ? 'text-destructive-foreground' : 'text-muted-foreground', className)}
    />
  );
}

/**
 * PickerPaymentAccount: a floating-label dropdown for choosing a payment account in the pay /
 * AutoPay flows. The shell is the shadcn `Select` primitive wearing a floating-label wrapper
 * (relative box + 48px trigger + floated `<label>`); on top it layers the picker affordances: a
 * leading card/mail/bank glyph, an EXPIRED method in destructive styling (selecting one is an
 * error), a Default badge, and an inline "Add new account" action at the foot of the menu.
 */
export function PickerPaymentAccount({
  items = [],
  value,
  onChange,
  onAddNew,
  label = 'Payment account',
  error = false,
  addNewLabel = 'Add new account',
  expiredLabel = 'Expired',
  defaultLabel = 'Default',
  className,
}: PickerPaymentAccountProps) {
  const triggerId = useId();
  const [foc, setFoc] = useState(false);
  const sel = items.find((i) => i.id === value) || items[0];
  const floated = foc || !!sel;

  const handleChange = (v: string | null) => {
    if (v == null) return;
    if (v === ADD_NEW) {
      onAddNew?.();
      return;
    }
    onChange?.(v);
  };

  const labelClass = cn(
    'pointer-events-none absolute left-9 z-10 whitespace-nowrap transition-all duration-150',
    error ? 'text-destructive-foreground' : foc ? 'text-foreground' : 'text-muted-foreground',
    floated ? 'top-1.5 text-xs' : 'top-1/2 -translate-y-1/2 text-sm',
  );

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: focus/blur only tracks the floating-label state; the Select owns keyboard/interaction semantics.
    <div
      data-slot="payment-account-picker"
      className={cn('min-w-0', className)}
      onFocus={() => setFoc(true)}
      onBlur={() => setFoc(false)}
    >
      <div className="relative [&_[data-slot=select]]:block [&_[data-slot=select]]:w-full">
        <Select value={sel ? sel.id : undefined} onValueChange={handleChange}>
          <SelectTrigger
            id={triggerId}
            aria-invalid={error || undefined}
            className={cn('relative h-12 w-full pl-9', error && 'border-destructive-foreground bg-destructive')}
          >
            <span
              className={cn(
                'pointer-events-none absolute left-3 top-1/2 -translate-y-1/2',
                sel?.expired ? 'text-destructive-foreground' : 'text-muted-foreground',
              )}
            >
              <AccountGlyph item={sel} size="md" />
            </span>
            <span className={cn('flex min-w-0 items-center gap-2', floated && 'translate-y-[7px]')}>
              <span
                className={cn(
                  'truncate text-sm font-medium',
                  sel?.expired ? 'text-destructive-foreground' : 'text-foreground',
                )}
              >
                {sel?.label}
              </span>
              {sel?.expired && (
                <span className={cn(PILL, 'bg-destructive text-destructive-foreground')}>
                  <Icon name="alertTriangle" weight="solid" size="xs" />
                  {expiredLabel}
                </span>
              )}
              {sel?.isDefault && !sel?.expired && (
                <span className={cn(PILL, 'bg-success text-success-foreground')}>
                  <Icon name="circleCheck" weight="solid" size="xs" />
                  {defaultLabel}
                </span>
              )}
            </span>
          </SelectTrigger>
          {/* No width override: the shared SelectContent already pins the popup to
              `w-(--anchor-width)` (the trigger's width). Forcing `!w-full` here would beat that and
              collapse the popup to its content, since 100% resolves against the content-sized
              positioner. The design's own copy needs `!w-full` only because its Select primitive
              doesn't set an anchor width. */}
          <SelectContent>
            {items.map((a) => (
              <SelectItem key={a.id} value={a.id} className="pr-8 pl-2">
                <span className="flex w-full items-center gap-3">
                  <AccountGlyph item={a} size="sm" className="shrink-0" />
                  <span className={cn('truncate', a.expired ? 'text-destructive-foreground' : 'text-foreground')}>
                    {a.label}
                  </span>
                  {a.expired && (
                    <span className={cn(PILL, 'ml-auto bg-destructive text-destructive-foreground')}>
                      <Icon name="alertTriangle" weight="solid" size="xs" />
                      {expiredLabel}
                    </span>
                  )}
                  {a.isDefault && !a.expired && (
                    <span className={cn(PILL, 'ml-auto bg-success text-success-foreground')}>
                      <Icon name="circleCheck" weight="solid" size="xs" />
                      {defaultLabel}
                    </span>
                  )}
                </span>
              </SelectItem>
            ))}
            {onAddNew && (
              <>
                <div className="my-1 -mx-1 h-px bg-border" />
                {/* `primary-foreground` is our dark on-primary ink (neutral-800), not white, so this
                    matches the design, where the action reads as regular dark text rather than orange. */}
                <SelectItem
                  value={ADD_NEW}
                  className="pr-8 pl-2 font-medium text-primary-foreground [&_svg]:text-primary-foreground"
                >
                  <span className="flex w-full items-center gap-3">
                    <Icon name="plus" weight="solid" size="sm" /> {addNewLabel}
                  </span>
                </SelectItem>
              </>
            )}
          </SelectContent>
        </Select>
        <label htmlFor={triggerId} className={labelClass}>
          {label}
        </label>
      </div>
    </div>
  );
}
